import React, { useState } from 'react';
import { X, Mail, Lock, User, Phone, Eye, EyeOff, ArrowRight } from 'lucide-react';
import api from '../utils/api';

const CustomerAuth = ({ isOpen, onClose, onAuthSuccess }) => {
  const [mode, setMode] = useState('login'); // login, signup, forgot, verification
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        if (!/^[6-9]\d{9}$/.test(formData.phone)) {
          setError('Please enter a valid 10-digit Indian phone number');
          setLoading(false);
          return;
        }

        const res = await api.post('/auth/register', {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone
        });

        if (res.data.success) {
          setSuccess(res.data.message || 'Account created! Please check your email to verify your account.');
          setMode('verification');
          setUnverifiedEmail(formData.email);
        }
      } else if (mode === 'login') {
        const res = await api.post('/auth/login', {
          email: formData.email,
          password: formData.password
        });

        if (res.data.success) {
          localStorage.setItem('token', res.data.data.token);
          localStorage.setItem('user', JSON.stringify(res.data.data.user));
          onAuthSuccess(res.data.data.user);
        }
      } else if (mode === 'forgot') {
        const res = await api.post('/customer/forgot-password', {
          email: formData.email
        });

        if (res.data.success) {
          setSuccess('Password reset link sent to your email!');
        }
      } else if (mode === 'verification') {
        // Resend verification email
        const res = await api.post('/auth/resend-verification', {
          email: unverifiedEmail || formData.email
        });

        if (res.data.success) {
          setSuccess('Verification email sent! Please check your inbox.');
        }
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.requiresVerification) {
        setError(errorData.message);
        setMode('verification');
        setUnverifiedEmail(errorData.email || formData.email);
      } else {
        setError(errorData?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-md bg-white border-4 border-black shadow-[16px_16px_0px_rgba(0,0,0,1)] animate-in fade-in zoom-in">
        {/* Header */}
        <div className="bg-yellow-400 p-6 border-b-4 border-black flex justify-between items-center">
          <h2 className="text-2xl font-black">
            {mode === 'login' && 'WELCOME BACK'}
            {mode === 'signup' && 'JOIN OWLFIT'}
            {mode === 'forgot' && 'FORGOT PASSWORD'}
            {mode === 'verification' && 'VERIFY YOUR EMAIL'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-black hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border-2 border-red-500 p-3 text-red-700 text-sm font-bold">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border-2 border-green-500 p-3 text-green-700 text-sm font-bold">
              {success}
            </div>
          )}

          {mode === 'verification' ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-black">
                <Mail size={28} className="text-black" />
              </div>
              <p className="text-sm mb-4">
                We sent a verification email to <strong>{unverifiedEmail || formData.email}</strong>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Please check your inbox and click the verification link to activate your account.
              </p>
              <p className="text-xs text-gray-500">
                Can't find it? Check your spam folder or resend the email below.
              </p>
            </div>
          ) : (
            <>
              {mode === 'signup' && (
                <div>
                  <label className="block text-xs font-black uppercase mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full pl-10 pr-4 py-3 border-2 border-black font-mono"
                      required
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-black uppercase mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full pl-10 pr-4 py-3 border-2 border-black font-mono"
                    required
                  />
                </div>
              </div>
            </>
          )}

          {mode !== 'verification' && mode === 'signup' && (
            <div>
              <label className="block text-xs font-black uppercase mb-2">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10-digit number"
                  maxLength={10}
                  className="w-full pl-10 pr-4 py-3 border-2 border-black font-mono"
                  required
                />
              </div>
            </div>
          )}

          {mode !== 'forgot' && mode !== 'verification' && (
            <div>
              <label className="block text-xs font-black uppercase mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-12 py-3 border-2 border-black font-mono"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          )}

          {mode === 'signup' && (
            <div>
              <label className="block text-xs font-black uppercase mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 border-2 border-black font-mono"
                  required
                />
              </div>
            </div>
          )}

          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="text-sm font-bold underline hover:no-underline"
              >
                Forgot password?
              </button>
            </div>
          )}

          {mode === 'verification' && unverifiedEmail && (
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Wrong email?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('login'); setUnverifiedEmail(''); setError(''); setSuccess(''); }}
                  className="font-bold underline hover:no-underline"
                >
                  Try different email
                </button>
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white py-4 font-black text-lg border-4 border-yellow-400 shadow-[4px_4px_0px_rgba(255,255,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? 'PLEASE WAIT...' : (
              <>
                {mode === 'login' && 'LOGIN'}
                {mode === 'signup' && 'CREATE ACCOUNT'}
                {mode === 'forgot' && 'SEND RESET LINK'}
                {mode === 'verification' && 'RESEND VERIFICATION EMAIL'}
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="p-6 pt-0 text-center">
          {mode === 'login' && (
            <p className="text-sm">
              Don't have an account?{' '}
              <button
                onClick={() => { setMode('signup'); setError(''); }}
                className="font-black underline hover:no-underline"
              >
                Sign up
              </button>
            </p>
          )}
          {mode === 'signup' && (
            <p className="text-sm">
              Already have an account?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); }}
                className="font-black underline hover:no-underline"
              >
                Login
              </button>
            </p>
          )}
          {mode === 'forgot' && (
            <p className="text-sm">
              Remember your password?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className="font-black underline hover:no-underline"
              >
                Back to login
              </button>
            </p>
          )}
          {mode === 'verification' && (
            <p className="text-sm">
              Already verified?{' '}
              <button
                onClick={() => { setMode('login'); setUnverifiedEmail(''); setError(''); setSuccess(''); }}
                className="font-black underline hover:no-underline"
              >
                Login now
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerAuth;
